import { Head, router } from '@inertiajs/react'
import dayjs from 'dayjs'
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Statistic, Tag, Typography } from 'antd'

import AdminLayout from '@/components/layouts/AdminLayout'
import { PageHeader } from '@/components/patterns'
import { Button, Card, Col, Empty, Row, Table } from '@/components/ui'

const { Text } = Typography

type AdminUser = {
  id: number
  email: string
  name?: string | null
  role: string
}

type DashboardStats = {
  total_events: number
  live_events: number
  draft_events: number
  archived_events: number
  total_registrants: number
  pending_registrants: number
  checked_in_registrants: number
  cancellation_requests: number
  event_owners: number
}

type UpcomingEvent = {
  id: number
  name: string
  slug: string | null
  start_date: string
  end_date: string
  status: 'draft' | 'live' | 'archived' | 'purged' | 'deleted'
  registrants: number
}

function statusColor(status: UpcomingEvent['status']) {
  switch (status) {
    case 'live':
      return 'green'
    case 'draft':
      return 'default'
    case 'archived':
      return 'blue'
    case 'purged':
    case 'deleted':
      return 'red'
    default:
      return 'default'
  }
}

function formatDate(date: string) {
  return dayjs(date).isValid() ? dayjs(date).format('MMM D, YYYY') : date
}

export default function AdminHome({
  user,
  stats,
  upcoming_events = [],
}: {
  user: AdminUser
  stats: DashboardStats
  upcoming_events?: UpcomingEvent[]
}) {
  const displayName = user?.name || user?.email || 'Administrator'

  return (
    <AdminLayout>
      <Head title="Admin" />
      <div className="w-full bg-[#f5f5f5]">
        <PageHeader
          title="Dashboard"
          subtitle={`Signed in as ${displayName}`}
          actions={
            <Button variant="primary" onClick={() => router.visit('/admin/events/new')}>
              Create Event
            </Button>
          }
        />

        <div className="p-6">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <Card hoverable className="h-full" onClick={() => router.visit('/admin/events')}>
                <Statistic title="Total Registrants" value={stats.total_registrants} prefix={<UserOutlined />} />
                <Button
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation()
                    router.visit('/admin/events')
                  }}
                >
                  View Events
                </Button>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <Card hoverable className="h-full" onClick={() => router.visit('/admin/events')}>
                <Statistic title="Total Events" value={stats.total_events} prefix={<CalendarOutlined />} />
                <Button
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation()
                    router.visit('/admin/events')
                  }}
                >
                  Manage Events
                </Button>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <Card hoverable className="h-full" onClick={() => router.visit('/admin/events')}>
                <Statistic title="Live Events" value={stats.live_events} prefix={<CheckCircleOutlined />} />
                <Text type="secondary">
                  Draft: {stats.draft_events.toLocaleString()} · Archived: {stats.archived_events.toLocaleString()}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <Card hoverable className="h-full" onClick={() => router.visit('/admin/event_owners')}>
                <Statistic title="Event Owners" value={stats.event_owners} prefix={<TeamOutlined />} />
                <Button
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation()
                    router.visit('/admin/event_owners')
                  }}
                >
                  Manage Owners
                </Button>
              </Card>
            </Col>


          </Row>

          <Row gutter={[16, 16]} className="mt-4">
            {/* <Col xs={24} lg={8}>
              <Card title="Quick Actions" className="h-full">
                <div className="flex flex-col gap-2">
                  <Button variant="secondary" onClick={() => router.visit('/admin/events')}>
                    Manage Events
                  </Button>
                  <Button variant="secondary" onClick={() => router.visit('/admin/event_owners')}>
                    Manage Event Owners
                  </Button>
                  <Button variant="secondary" onClick={() => router.visit('/admin/categories')}>
                    Manage Categories
                  </Button>
                  <Button variant="secondary" onClick={() => router.visit('/admin/badge_printings')}>
                    Badge Printings
                  </Button>
                </div>
              </Card>
            </Col> */}

            <Col xs={24} lg={24}>
              <Card title="Upcoming Events" className="h-full">
                {upcoming_events.length === 0 ? (
                  <Empty description="No upcoming events." />
                ) : (
                  <Table<UpcomingEvent>
                    rowKey="id"
                    pagination={false}
                    dataSource={upcoming_events}
                    columns={[
                      {
                        title: 'Event',
                        dataIndex: 'name',
                        key: 'name',
                        render: (_value, record) => (
                          <Button variant="text" onClick={() => router.visit(`/admin/events/${record.id}`)}>
                            {record.name}
                          </Button>
                        ),
                      },
                      {
                        title: 'Dates',
                        key: 'dates',
                        render: (_value, record) => (
                          <Text type="secondary">
                            {formatDate(record.start_date)} – {formatDate(record.end_date)}
                          </Text>
                        ),
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        render: (value: UpcomingEvent['status']) => <Tag color={statusColor(value)}>{value}</Tag>,
                      },
                      {
                        title: 'Registrants',
                        dataIndex: 'registrants',
                        key: 'registrants',
                        align: 'right',
                        render: (value: number) => value.toLocaleString(),
                      },
                    ]}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </AdminLayout>
  )
}
